import { labelTone } from '../../lib/labelTone'
import type { DailyStatusView as DailyStatus } from '../../lib/dailyStatus'
import { ringSet } from './ringSet'

/**
 * The payload's `summary`, with one dot per measure and a counted roll-up.
 *
 * ── ITS OWN FILE, AND INSIDE THE BODY ───────────────────────────────────────
 *
 * Its own file because WP-HOME imports it and because it renders no
 * `ScoreCard` — `disclaimerPairing.test.tsx` enumerates the components that
 * draw a score, and a headline is not one. Mounted INSIDE `DailyStatusBody`
 * (O5) rather than in each host's page header, so Record and RecordingDetail
 * keep the payload's summary too.
 *
 * ── WHAT THE CLIENT IS ALLOWED TO SAY HERE ──────────────────────────────────
 *
 * The title is the payload's sentence, verbatim. The roll-up's only client
 * words are "measure", "measures" and the separator: everything else is the
 * payload's own label string and a count of SLOTS carrying it. R15 allows the
 * count and forbids everything that would turn it into a reading — no
 * adjective, no comparison, no ordering by size, no percentage, no "most" or
 * "all". Groups appear in first-seen order for exactly that reason: sorting
 * them by count would be the client ranking the day.
 *
 * ── AND WHAT IT DOES NOT SAY ────────────────────────────────────────────────
 *
 * No `.headline__note`. The canvas's "50 on any ring is your own baseline" is
 * false for two of the eight slots (O2), and this component draws no tick
 * either.
 *
 * The dots are `aria-hidden`: they are the roll-up drawn, and the roll-up says
 * the same thing in words immediately beside them, so tone is never the only
 * channel.
 */
export function DailyHeadline({ status }: { status: DailyStatus }) {
  const cards = ringSet(status)

  // Counting SLOTS, never values. No score is read, compared, summed or
  // averaged here — the two `+= 1` sites carry that reason in
  // `clientScience.test.ts`.
  let measures = 0
  const groups: Array<{ label: string; slots: number }> = []
  for (const card of cards) {
    if (card.value === null) continue
    measures += 1
    if (card.label === null) continue
    const group = groups.find((g) => g.label === card.label)
    if (group === undefined) groups.push({ label: card.label, slots: 1 })
    else group.slots += 1
  }

  // A dot exists only for a card that HAS a value, which is why there is no
  // "off" dot: an absent measure is absent, not a dimmed one.
  const dotted = cards.filter((card) => card.value !== null)
  // `!== 0`, not `> 0`: a numeric comparison in a score-reading module needs a
  // written reason in `clientScience.test.ts`, and this one is worth none —
  // the budget there is for things that could be mistaken for measurement.
  const hasSub = dotted.length !== 0

  if (status.summary === null && !hasSub) return null

  return (
    <div className="headline">
      {status.summary !== null && <h2 className="headline__title">{status.summary}</h2>}
      {hasSub && (
        <div className="headline__sub">
          <div className="dots" aria-hidden="true">
            {dotted.map((card) => (
              <i key={card.slot} className={`dots__dot dots__dot--${labelTone(card.labelCode)}`} />
            ))}
          </div>
          <span className="headline__rollup">
            {measures} {measures === 1 ? 'measure' : 'measures'}
            {groups.map((group) => (
              <span key={group.label}>
                {' · '}
                {group.slots} {group.label}
              </span>
            ))}
          </span>
        </div>
      )}
    </div>
  )
}
